import { TNativeSdkConfigurations } from '../../@pano-project/types/IGateway';
import { getNativeSdkConfigurations } from '../../utils/hooks/digital-wallets/native-sdk';

// PRX-24667: everything that happens with a native-SDK method (Revolut Pay)
// lives here, so any component can read it. The SDK objects themselves are
// not serializable and stay in the hook modules.

export type TNativeSdkMode = 'prod' | 'sandbox';
export type TNativeSdkScriptStatus = 'idle' | 'loading' | 'loaded' | 'failed';
export type TNativeSdkButtonState = 'hidden' | 'loading' | 'ready' | 'processing' | 'error';
export type TNativeSdkPayRequestStatus = 'none' | 'in_progress' | 'transaction' | 'failed';

export interface INativeSdkState {
  // native_sdk_configurations of the selected gateway (null when it has none)
  configurations: TNativeSdkConfigurations | null;
  // script status per mode; lives for the page session (never reset)
  scriptStatus: Record<TNativeSdkMode, TNativeSdkScriptStatus>;
  buttonState: TNativeSdkButtonState;
  failureCount: number;
  lastErrorCause: string | null;
  // the "payment in progress" mark
  isPaymentInProgress: boolean;
  clickedAt: number | null;
  // the pay request of the current click
  payRequest: { status: TNativeSdkPayRequestStatus; traceId: string | null };
  // the values the SDK button is mounted with (amount in the smallest unit)
  mounted: { amount: number | null; currency: string | null };
  // the message of the last ended attempt shown in the block (failed pay
  // request); cleared when a new attempt starts
  attemptMessage: string | null;
}

export const NATIVE_SDK_MAX_FAILURES = 3;

export const nativeSdkInitialState: INativeSdkState = {
  configurations: null,
  scriptStatus: { prod: 'idle', sandbox: 'idle' },
  buttonState: 'hidden',
  failureCount: 0,
  lastErrorCause: null,
  isPaymentInProgress: false,
  clickedAt: null,
  payRequest: { status: 'none', traceId: null },
  mounted: { amount: null, currency: null },
  attemptMessage: null,
};

type TGatewayIdentity =
  | {
      payment_method_key?: string | null;
      option_id?: number | null;
      option_type?: string | null;
    }
  | false
  | null
  | undefined;

// Same rule as `isGenuineMethodSwitch` in change-pay-info.js: `state` is the
// previous store tree, so a CREATE_CURRENT_GATEWAY for the same selection (the
// list item first, then its details) is not a switch. The gateway ids are
// compared too, because several gateways can share one method key.
const isGenuineGatewaySwitch = (previous: TGatewayIdentity, next: TGatewayIdentity): boolean => {
  const before = previous || {};
  const after = next || {};

  return (
    before.payment_method_key !== after.payment_method_key ||
    before.option_id !== after.option_id ||
    before.option_type !== after.option_type
  );
};

const nativeSdkState = (state: any, action: { type: string; payload?: any }): INativeSdkState => {
  if (state === undefined) {
    return nativeSdkInitialState;
  }
  const current: INativeSdkState = state.nativeSdk || nativeSdkInitialState;

  switch (action.type) {
    case 'CREATE_CURRENT_GATEWAY': {
      const configurations = getNativeSdkConfigurations(action.payload || null);

      if (isGenuineGatewaySwitch(state.currentGateway, action.payload)) {
        return { ...nativeSdkInitialState, scriptStatus: current.scriptStatus, configurations };
      }
      return { ...current, configurations };
    }
    case 'NATIVE_SDK_SET_SCRIPT_STATUS':
      return {
        ...current,
        scriptStatus: { ...current.scriptStatus, [action.payload.mode]: action.payload.status },
      };
    case 'NATIVE_SDK_SET_BUTTON_STATE':
      return { ...current, buttonState: action.payload };
    case 'NATIVE_SDK_REGISTER_FAILURE':
      return {
        ...current,
        buttonState: 'error',
        failureCount: current.failureCount + 1,
        lastErrorCause: action.payload,
      };
    case 'NATIVE_SDK_SET_PAYMENT_IN_PROGRESS':
      // Setting the mark starts a new attempt, so the pay request of the
      // previous attempt is forgotten.
      return action.payload
        ? {
            ...current,
            isPaymentInProgress: true,
            payRequest: nativeSdkInitialState.payRequest,
            attemptMessage: null,
          }
        : { ...current, isPaymentInProgress: false };
    case 'NATIVE_SDK_SET_ATTEMPT_MESSAGE':
      return { ...current, attemptMessage: action.payload ?? null };
    case 'NATIVE_SDK_SET_CLICKED_AT':
      return { ...current, clickedAt: action.payload };
    case 'NATIVE_SDK_SET_PAY_REQUEST':
      return {
        ...current,
        payRequest: { status: action.payload.status, traceId: action.payload.traceId ?? null },
      };
    case 'NATIVE_SDK_SET_MOUNTED':
      return {
        ...current,
        mounted: { amount: action.payload.amount, currency: action.payload.currency },
      };
    default:
      return current;
  }
};

// Read helpers for components and hooks.
export const selectNativeSdk = (state: any): INativeSdkState =>
  state?.nativeSdk || nativeSdkInitialState;

export const selectNativeSdkScriptStatus = (
  state: any,
  mode: TNativeSdkMode,
): TNativeSdkScriptStatus => selectNativeSdk(state).scriptStatus[mode] || 'idle';

export const selectNativeSdkButtonState = (state: any): TNativeSdkButtonState =>
  selectNativeSdk(state).buttonState;

export const selectIsNativeSdkPaymentInProgress = (state: any): boolean =>
  selectNativeSdk(state).isPaymentInProgress;

export const selectNativeSdkPayRequest = (state: any) => selectNativeSdk(state).payRequest;

export const selectNativeSdkMounted = (state: any) => selectNativeSdk(state).mounted;

export const selectNativeSdkAttemptMessage = (state: any): string | null =>
  selectNativeSdk(state).attemptMessage ?? null;

// The selected gateway by the fields of `isGenuineGatewaySwitch`: it changes
// when a switch resets this state (also the button state to 'hidden').
export const selectNativeSdkSelectionKey = (state: any): string => {
  const { payment_method_key, option_id, option_type } = (state?.currentGateway || {}) as Exclude<
    TGatewayIdentity,
    false | null | undefined
  >;
  return [payment_method_key, option_id, option_type].join('|');
};

export const selectCanRetryNativeSdk = (state: any): boolean =>
  selectNativeSdk(state).failureCount < NATIVE_SDK_MAX_FAILURES;

export { nativeSdkState };
